/**
 * tutorLiveRoomAdapter — la salle du tuteur, du serveur et du moteur aux formes de `<TutorLivePage>` et
 * de `<TutorLiveOverlays>` : moi sur ma vignette, la nature de la salle, l'heure du début, le prénom du
 * champion qui aide en séance, et les mots des boîtes que la salle pose (le débordement, la sortie
 * forcée, la notification d'une autre séance, les permissions refusées). Jamais importé par le DS.
 *
 * Les phrases sont celles de l'ancien live, citées à chaque fonction.
 */
import moment from "moment"

import { championSlugsToNames } from "../../../../../data/constants/championsConstants"
import { type ApiTutorOverrun } from "../../../../../engine/live/tutor/tutorRoomRules"
import { type TutorTapOutcome } from "../../../../../engine/live/tutor/tutorLiveNotificationTap"
import { type TutorBrowser } from "../../../../../engine/live/tutor/tutorMediaAccess"
import { closestNextOccurrence, type LiveSessionTimeLike } from "../../../../../engine/live/student/liveSessionTime"
import { type RoomExit } from "../../../../../engine/live/room/roomMachine"
import { type ChampionName } from "../../../../../design-system/atoms/Avatar/ChampionAvatar"
import { type UITutorLiveDialog } from "../../../../../design-system/templates/TutorLiveOverlays/TutorLiveOverlays"
import { type UIGuidanceLine } from "../../../../../design-system/molecules/DeviceCheckGuidance/DeviceCheckGuidance"
import { type UILiveParticipant } from "../../../ui/types/studentLive"
import { type UITutorLiveRoomKind } from "../../../ui/types/tutorLive"
import { avatarOf } from "../../student/Live/studentLiveAdapter"

/**
 * Le champion qui aide en séance, par son identifiant (une clé, pas un texte) : son prénom vient du
 * monde de jeu (`championSlugsToNames`), comme l'espace élève le lit (`questAdapter`).
 */
export const LIVE_HELPER_SLUG: ChampionName = "sam"

export function tutorHelperName(): string {
    return (championSlugsToNames as Record<string, string>)[LIVE_HELPER_SLUG] ?? ""
}

export type ApiTutorAccount = {
    id?: number | string | null
    firstname?: string | null
    display_name?: string | null
    avatar?: string | null
}

/**
 * Moi, sur ma vignette (`TutorLiveScreen.js:395-413`) : ma caméra et mon micro tels que la salle les
 * publie ; sans salle encore, coupés. La vignette ne porte ni pastille ni bouton (S-123).
 */
export function toTutorLiveMe(
    account: ApiTutorAccount | null | undefined,
    local: { cameraOn: boolean; micOn: boolean } | null
): UILiveParticipant {
    const firstname = account?.firstname?.trim()
    return {
        id: String(account?.id ?? "me"),
        name:
            firstname != null && firstname !== ""
                ? firstname
                : ((account?.display_name ?? "").trim().split(/\s+/)[0] ?? ""),
        avatarUrl: avatarOf(account?.avatar),
        tutor: true,
        cameraOff: local?.cameraOn !== true,
        micOff: local?.micOn !== true,
    }
}

/** La nature de la salle (`live_room.type`) : individuelle, d'essai, sinon partagée. */
export function toTutorRoomKind(type: string | null | undefined): UITutorLiveRoomKind {
    if (type === "individual") return "individual"
    if (type === "trial") return "trial"
    return "shared"
}

/**
 * L'heure du début du live (ms), pour le chrono du bandeau : la prochaine occurrence non finalisée
 * (`findClosestNextOccurrence`, l'heure que l'ancien pied affichait) ; sans elle, l'entrée dans la salle.
 */
export function tutorLiveStartedAt(session: LiveSessionTimeLike | null | undefined, enteredAt: number): number {
    const date = closestNextOccurrence(session)?.date
    if (date == null) return enteredAt
    const start = moment(date)
    return start.isValid() ? start.valueOf() : enteredAt
}

/* ── le débordement ── */

/** Au-delà de trois prénoms, deux et le compte du reste (`tutorOverrunCopy.formatStudentNames`). */
export function formatStudentNames(names: readonly string[]): string {
    if (names.length === 0) return ""
    if (names.length === 1) return names[0]
    if (names.length === 2) return `${names[0]} et ${names[1]}`
    if (names.length === 3) return `${names[0]}, ${names[1]} et ${names[2]}`
    return `${names[0]}, ${names[1]} et ${names.length - 2} autres`
}

/**
 * La boîte du débordement (`tutorOverrunCopy.buildTutorOverrunCopy`) : qui attend déjà là-bas
 * (`p1_names`), qui y est attendu mais reste ici (`p2_names`), puis le geste — clôturer quand il retient
 * des élèves, rejoindre sinon. `null` quand personne n'attend ni n'est attendu.
 */
export function toOverrunDialog(overrun: ApiTutorOverrun | null | undefined): UITutorLiveDialog | null {
    const waiting = overrun?.p1_names ?? []
    const stayed = overrun?.p2_names ?? []
    if (overrun == null || (waiting.length === 0 && stayed.length === 0)) return null
    const label = overrun.next_occurrence_label ?? ""
    const lines: string[] = []
    if (waiting.length > 0) {
        const names = formatStudentNames(waiting)
        lines.push(waiting.length === 1 ? `${names} est déjà dans la salle.` : `${names} sont déjà dans la salle.`)
    }
    if (stayed.length > 0) {
        const names = formatStudentNames(stayed)
        lines.push(
            stayed.length === 1
                ? `${names} est encore avec toi, alors qu'on l'attend là-bas.`
                : `${names} sont encore avec toi, alors qu'on les attend là-bas.`
        )
    }
    lines.push(
        stayed.length > 0 ? `Clôture cette séance et ouvre ton live de ${label}.` : "Rejoins la salle dès que tu peux."
    )
    return { kind: "overrun", title: `Ton live de ${label} a commencé`, lines }
}

/* ── la sortie forcée ── */

const EXIT_TEXTS = {
    goneTitle: "Cette session n'est plus disponible",
    disconnected: "Tu as été déconnecté du live",
    forbidden: "Ce live n'est accessible que le jour même.",
    payment: "L'accès aux lives est suspendu en attendant un paiement.",
    ok: "OK",
}

/**
 * La boîte de la sortie forcée : la séance disparue (`LiveMainLayout.js:213-224`, « Cette session n'est
 * plus disponible » / « Tu as été déconnecté du live ») ; pas le bon jour ou un paiement en attente, le
 * message du serveur, que l'ancien disait en toast (`useApiStatus(…, true)`), à défaut le nôtre.
 */
export function toExitDialog(exit: { exit: RoomExit; message: string | null }): UITutorLiveDialog {
    if (exit.exit === "gone") {
        return {
            kind: "custom",
            title: EXIT_TEXTS.goneTitle,
            message: EXIT_TEXTS.disconnected,
            confirmLabel: EXIT_TEXTS.ok,
        }
    }
    const fallback = exit.exit === "payment" ? EXIT_TEXTS.payment : EXIT_TEXTS.forbidden
    const message = exit.message != null && exit.message.trim() !== "" ? exit.message.trim() : fallback
    return { kind: "custom", title: EXIT_TEXTS.disconnected, message, confirmLabel: EXIT_TEXTS.ok }
}

/* ── une notification d'une autre séance ── */

/**
 * La boîte d'une notification touchée qui vient d'une autre séance
 * (`NotificationSubscriptionHandler.js:186-191, 232-237, 263-268`). `null` : l'issue n'en pose pas.
 */
export function toTapDialog(outcome: TutorTapOutcome | null | undefined): UITutorLiveDialog | null {
    if (outcome?.kind === "other_session_message") {
        return {
            kind: "custom",
            title: "Message reçu dans une autre session",
            message: outcome.group
                ? "Pour y accéder, il faut quitter cette session live et ouvrir l'autre"
                : "Pour y accéder, il faut quitter cette session live",
            confirmLabel: EXIT_TEXTS.ok,
        }
    }
    if (outcome?.kind === "other_session_call") {
        return {
            kind: "custom",
            title: "Appel reçu dans une autre session",
            message: "Pour y accéder, il faut quitter cette session et ouvrir l'autre",
            confirmLabel: EXIT_TEXTS.ok,
        }
    }
    return null
}

/* ── les permissions refusées ── */

const PERSISTS =
    "Si le problème persiste, vérifie aussi les paramètres de confidentialité et de sécurité de ton système."

/**
 * Où rendre la caméra et le micro, pour le navigateur de la personne : les pas de l'ancien guide
 * (`PermissionsManager.renderGuide`, jamais appelé), ici montrés quand le navigateur a refusé.
 */
export function toPermissionsDialog(browser: TutorBrowser): UITutorLiveDialog {
    const steps: Record<TutorBrowser, string[]> = {
        Chrome: [
            "Clique sur l'icône à gauche de la barre d'adresse.",
            "Cherche « Microphone » et « Caméra » dans le menu déroulant et choisis « Autoriser ».",
        ],
        Firefox: [
            "Clique sur l'icône de la caméra dans la barre d'adresse.",
            "Choisis « Autoriser » dans le menu déroulant.",
        ],
        Safari: [
            "Ouvre les Réglages de Safari, onglet « Sites web ».",
            "Sous « Micro » et « Caméra », choisis « Autoriser » pour app.masteur.com.",
        ],
        Edge: [
            "Clique sur l'icône du cadenas à gauche de l'adresse.",
            "Sous « Microphone » et « Caméra », choisis « Autoriser » dans le menu déroulant.",
        ],
        other: [],
    }
    const lines: UIGuidanceLine[] = [...steps[browser], PERSISTS].map((text) => ({ text }))
    return { kind: "permissions", steps: lines }
}
